// src/pages/Projeler/ProjelerPage.tsx (veya mevcut konumu)
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { postRequest } from "../../../api/postRequest";
import { useTheme } from "../../../context/ThemeContext";
import { Search, X } from "lucide-react";

/* ==== Types (public) ==== */
interface ApiResponse<T> {
  success: boolean;
  errorCode?: string;
  errorDetail?: string;
  data?: T;
}

type Fit = "cover" | "contain";

interface ProjeCardDto {
  id: number;
  baslik: string;
  aciklama?: string | null;
  kapakFotoUrl?: string | null;
  siraNo: number;
  aktifMi: boolean;
  olusturmaTarihi: string;
  guncellemeTarihi?: string | null;
}

interface ProjeListeRequest {
  q?: string;
  aktifMi?: boolean | null;
  page: number;
  pageSize: number;
  sort?: "sira" | "yeni" | "eski" | string;
}
interface ProjeListeResponse {
  items: ProjeCardDto[];
  total: number;
  page: number;
  pageSize: number;
}

/* ==== UI helpers ==== */
const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

/* ==== Color helpers (koyu/açık zemin algısı + surface tonları) ==== */
const hexToRgb = (hex: string) => {
  const h = (hex || "#0f1115").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const luminance = ({ r, g, b }: { r: number; g: number; b: number }) => {
  const f = (v: number) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};

/* ==== Kart ==== */
const ProjectCard: React.FC<{
  item: ProjeCardDto;
  accent: string;
  textPrimary: string;
  textSecondary: string;
  divider: string;
  surface1: string;
  surface2: string;
}> = ({ item, accent, textPrimary, textSecondary, divider, surface1, surface2 }) => {
  return (
    <Link
      to={`/projeler/${item.id}`}
      className="group rounded-2xl overflow-hidden border transition-all hover:-translate-y-0.5 hover:shadow-xl focus:outline-none"
      style={{ backgroundColor: surface1, borderColor: divider }}
    >
      <div className="relative aspect-[16/10] overflow-hidden" style={{ backgroundColor: surface2 }}>
        {item.kapakFotoUrl ? (
          <img
            src={item.kapakFotoUrl}
            alt={item.baslik}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-sm" style={{ color: textSecondary }}>
            Kapak görseli yok
          </div>
        )}

        {/* Hafif gradient – yazı okunurluğu için (görsel varsa) */}
        <div
          className="absolute inset-x-0 bottom-0 h-20 pointer-events-none"
          style={{
            background:
              "linear-gradient(to top, rgba(0,0,0,.35), rgba(0,0,0,0))",
            opacity: item.kapakFotoUrl ? 0.9 : 0,
          }}
        />
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <h3
            className="text-[15px] sm:text-base font-normal  leading-snug line-clamp-2"
            style={{ color: textPrimary }}
            title={item.baslik}
          >
            {item.baslik}
          </h3>

          <span
            className="shrink-0 text-[11px] px-2 py-1 rounded-full border"
            style={{
              color: accent,
              borderColor: `${accent}55`,
              background: `${accent}14`,
            }}
          >
            Proje
          </span>
        </div>

        <p className="text-sm mt-2 line-clamp-2" style={{ color: textSecondary }}>
          {item.aciklama || "—"}
        </p>

        {/* küçük meta satırı (istersen kaldırabilirsin) */}
        <div className="mt-3 text-[12px]" style={{ color: textSecondary }}>
          Sıra: <span style={{ color: textPrimary, fontWeight: 600 }}>{item.siraNo}</span>
        </div>
      </div>
    </Link>
  );
};

const ProjelerPage: React.FC = () => {
  const { theme } = useTheme();

  const accent = theme?.accent ?? "#22c55e";
  const textPrimary = theme?.textPrimary ?? "#111827";
  const textSecondary = theme?.textSecondary ?? "#6b7280";

  // theme-aware surfaces
  const bgHex = theme?.backgroundColor || "#0f1115";
  const isDark = luminance(hexToRgb(bgHex)) < 0.5;

  const surface1 = isDark ? "rgba(255,255,255,.035)" : "rgba(0,0,0,.035)";
  const surface2 = isDark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.06)";
  const surface3 = isDark ? "rgba(255,255,255,.085)" : "rgba(0,0,0,.085)";
  const divider = isDark ? "rgba(255,255,255,.10)" : "rgba(0,0,0,.12)";
  const softShadow = isDark ? "0 10px 28px rgba(0,0,0,.28)" : "0 10px 28px rgba(0,0,0,.10)";

  // list state
  const [items, setItems] = useState<ProjeCardDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [sort, setSort] = useState<"yeni" | "eski" | "sira">("yeni");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const fetchList = async (opts?: { forcePage?: number }) => {
    const nextPage = opts?.forcePage ?? page;

    try {
      setLoading(true);

      const body: ProjeListeRequest = {
        q: q.trim() || undefined,
        aktifMi: true,
        page: nextPage,
        pageSize,
        sort,
      };

      const res = await postRequest<ProjeListeRequest, ApiResponse<ProjeListeResponse>>(
        "/Projeler/listele",
        body
      );

      if (res.success && res.data) {
        setItems(res.data.items);
        setTotal(res.data.total);
      } else {
        setItems([]);
        setTotal(0);
      }
    } catch (e) {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sort]);

  const onApplySearch = () => {
    setPage(1);
    fetchList({ forcePage: 1 });
  };

  const onClearSearch = () => {
    if (!q) return;
    setQ("");
    setPage(1);
    // q state async; temiz body için microtask yerine doğrudan requestte q.trim() kullanıldığı için sorun yok
    setTimeout(() => fetchList({ forcePage: 1 }), 0);
  };

  return (
    <div style={{ backgroundColor: theme?.backgroundColor }}>
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-10">
        {/* =================== HERO =================== */}
        <div
          className="rounded-3xl p-[1px] mb-8"
          style={{
            background: theme?.gradientHero ?? "linear-gradient(90deg,#0ea5e9,#22c55e)",
          }}
        >
          <div
            className="rounded-3xl p-5 sm:p-6 md:p-8 backdrop-blur"
            style={{ background: isDark ? "rgba(0,0,0,.28)" : "rgba(255,255,255,.65)" }}
          >
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
              <div>
                <span
                  className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full mb-3 border"
                  style={{
                    background: `${accent}14`,
                    color: accent,
                    borderColor: `${accent}33`,
                  }}
                >
                  portföy
                </span>

                <h1
                  className="text-3xl md:text-4xl font-normal  leading-tight"
                  style={{ color: textPrimary }}
                >
                  Projelerimiz
                </h1>

                <p className="mt-2 max-w-2xl text-sm md:text-base" style={{ color: textSecondary }}>
                  3B baskı, tarama ve tersine mühendislik projelerimizden seçkiler.
                </p>
              </div>

              {/* hero quick-filters */}
              <div
                className="w-full lg:w-[560px] rounded-2xl border p-3 sm:p-4"
                style={{ backgroundColor: surface1, borderColor: divider, boxShadow: softShadow }}
              >
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                  {/* search */}
                  <div
                    className="sm:col-span-12 md:col-span-6 flex items-center gap-2 rounded-xl px-3 py-2 border"
                    style={{ backgroundColor: surface2, borderColor: divider }}
                  >
                    <Search className="w-4 h-4" style={{ color: textSecondary }} />
                    <input
                      className="outline-none text-sm w-full bg-transparent"
                      placeholder="Proje ara…"
                      style={{ color: textPrimary }}
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") onApplySearch();
                      }}
                    />
                    {q && (
                      <button
                        type="button"
                        onClick={onClearSearch}
                        className="p-1 rounded-md opacity-80 hover:opacity-100"
                        aria-label="Aramayı temizle"
                        style={{ color: textSecondary }}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* sort */}
                  <select
                    className="sm:col-span-6 md:col-span-3 rounded-xl px-3 py-2 text-sm bg-transparent border outline-none"
                    style={{ color: textPrimary, borderColor: divider }}
                    value={sort}
                    onChange={(e) => {
                      setSort(e.target.value as any);
                      setPage(1);
                    }}
                  >
                    <option value="yeni">En Yeni</option>
                    <option value="eski">En Eski</option>
                    <option value="sira">Sıra No</option>
                  </select>

                  {/* page size */}
                  <select
                    className="sm:col-span-6 md:col-span-3 rounded-xl px-3 py-2 text-sm bg-transparent border outline-none"
                    style={{ color: textPrimary, borderColor: divider }}
                    value={pageSize}
                    onChange={(e) => {
                      const v = clamp(parseInt(e.target.value) || 12, 6, 48);
                      setPageSize(v);
                      setPage(1);
                    }}
                  >
                    {[6, 12, 24, 36].map((n) => (
                      <option key={n} value={n}>
                        {n} / sayfa
                      </option>
                    ))}
                  </select>

                  {/* action */}
                  <button
                    type="button"
                    onClick={onApplySearch}
                    className="sm:col-span-12 rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition-transform active:scale-[0.99]"
                    style={{ background: accent }}
                    disabled={loading}
                  >
                    {loading ? "Yükleniyor…" : "Uygula"}
                  </button>
                </div>

                <div className="mt-3 flex items-center justify-between text-xs" style={{ color: textSecondary }}>
                  <span>{loading ? "Liste yenileniyor…" : `Toplam ${total} kayıt`}</span>
                  <span>
                    Sayfa{" "}
                    <b style={{ color: textPrimary }}>{page}</b> / {totalPages}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* =================== GRID =================== */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="rounded-2xl overflow-hidden border animate-pulse"
                style={{ backgroundColor: surface1, borderColor: divider }}
              >
                <div className="aspect-[16/10]" style={{ backgroundColor: surface2 }} />
                <div className="p-4 space-y-2">
                  <div className="h-5 rounded w-2/3" style={{ backgroundColor: surface2 }} />
                  <div className="h-3 rounded w-4/5" style={{ backgroundColor: surface2 }} />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div
            className="rounded-2xl border p-5"
            style={{ backgroundColor: surface1, borderColor: divider }}
          >
            <p className="text-sm" style={{ color: textSecondary }}>
              Sonuç bulunamadı.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {items.map((it) => (
              <ProjectCard
                key={it.id}
                item={it}
                accent={accent}
                textPrimary={textPrimary}
                textSecondary={textSecondary}
                divider={divider}
                surface1={surface1}
                surface2={surface2}
              />
            ))}
          </div>
        )}

        {/* =================== PAGINATION =================== */}
        <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-sm" style={{ color: textSecondary }}>
            Toplam{" "}
            <b style={{ color: textPrimary }}>{total}</b> kayıt
          </div>

          <div className="flex items-center justify-between sm:justify-end gap-2">
            <button
              type="button"
              onClick={() => canPrev && setPage((p) => p - 1)}
              disabled={!canPrev}
              className="px-3 py-2 rounded-xl border text-sm font-semibold"
              style={{
                borderColor: divider,
                backgroundColor: canPrev ? surface1 : "transparent",
                color: textPrimary,
                opacity: canPrev ? 1 : 0.5,
                cursor: canPrev ? ("pointer" as const) : ("not-allowed" as const),
              }}
            >
              Önceki
            </button>

            <span className="text-sm px-3" style={{ color: textSecondary }}>
              {page} / {totalPages}
            </span>

            <button
              type="button"
              onClick={() => canNext && setPage((p) => p + 1)}
              disabled={!canNext}
              className="px-3 py-2 rounded-xl border text-sm font-semibold"
              style={{
                borderColor: divider,
                backgroundColor: canNext ? surface1 : "transparent",
                color: textPrimary,
                opacity: canNext ? 1 : 0.5,
                cursor: canNext ? ("pointer" as const) : ("not-allowed" as const),
              }}
            >
              Sonraki
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjelerPage;
